import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../context/AuthContext';
import api, { stlAPI } from '../lib/api';
import QuotaErrorBanner from '../components/QuotaErrorBanner';
import { ROICard } from '../components/ROIDisplay';
import OnboardingTour from '../components/OnboardingTour';
import { OrientationControls } from '../components/OrientationControls';
import {
  Upload,
  FileText,
  Search,
  Wrench,
  Layers,
  CheckCircle,
  AlertTriangle,
  Loader,
  X,
  Download,
  Eye,
  ChevronRight,
  ArrowRight,
  RefreshCw,
  Settings2,
  FileDown,
  Zap,
  Lock,
  Flame,
} from 'lucide-react';
import { STLViewerWithControls } from '../components/STLViewer';
import { trackFileUpload, trackSupportGeneration, trackDownload } from '../lib/analytics';
import { EmailVerificationBanner } from '../components/EmailVerificationBanner';

// Step definitions
const STEPS = {
  UPLOAD: 0,
  ANALYZING: 1,
  ANALYSIS_RESULT: 2,
  REPAIRING: 3,
  REPAIR_DONE: 4,
  CONFIGURE: 5,
  GENERATING: 6,
  RESULTS: 7,
};

const stepLabelsAll = [
  { key: 'upload', label: 'Upload', icon: Upload },
  { key: 'analyze', label: 'Analyze', icon: Search },
  { key: 'repair', label: 'Repair', icon: Wrench },
  { key: 'generate', label: 'Generate', icon: Layers },
];

const stepLabelsRepair = [
  { key: 'upload', label: 'Upload', icon: Upload },
  { key: 'analyze', label: 'Analyze', icon: Search },
  { key: 'repair', label: 'Repair', icon: Wrench },
];

const DEFAULT_PRICES = {
  PLA: 20, ABS: 22, PETG: 25, TPU: 35, PVA: 60, HIPS: 25,
};

const UnifiedWorkflow = ({ mode = 'full' }) => {
  const { user } = useAuth();
  const isRepairOnly = mode === 'repair-only';

  // Step state
  const [currentStep, setCurrentStep] = useState(STEPS.UPLOAD);

  // File state
  const [file, setFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  // Analysis state
  const [analysisResult, setAnalysisResult] = useState(null);
  const [hasIssues, setHasIssues] = useState(false);

  // Repair state
  const [repairResult, setRepairResult] = useState(null);

  // Support generation state
  const [treeStyle, setTreeStyle] = useState('forest');
  const [optimizeOrientation, setOptimizeOrientation] = useState(true);
  const [manualRotation, setManualRotation] = useState({ x: 0, y: 0, z: 0 });
  const [density, setDensity] = useState('standard');
  const [latticeSpacing, setLatticeSpacing] = useState(2.0);
  const [latticeWidth, setLatticeWidth] = useState(0.6);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [supportResult, setSupportResult] = useState(null);
  const [show3DPreview, setShow3DPreview] = useState(false);

  // Progress state
  const [progressInfo, setProgressInfo] = useState({ stage: '', percent: 0, elapsed: 0 });

  // General state
  const [error, setError] = useState('');
  const [customPrices, setCustomPrices] = useState(DEFAULT_PRICES);

  // Quote state
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteName, setQuoteName] = useState('');
  const [quoteMarkup, setQuoteMarkup] = useState(50);
  const [quoteNotes, setQuoteNotes] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Plan gating
  const isFreePlan = !user?.subscription_plan || user.subscription_plan === 'free';
  const [showUpgradeHint, setShowUpgradeHint] = useState(false);

  // Load user preferences on mount
  useEffect(() => {
    const loadPrefs = async () => {
      try {
        const [priceRes, prefRes] = await Promise.allSettled([
          api.get('/user/preferences/filament-prices'),
          api.get('/user/preferences/support-settings'),
        ]);
        if (priceRes.status === 'fulfilled' && priceRes.value.data.prices) {
          setCustomPrices(priceRes.value.data.prices);
        }
        if (prefRes.status === 'fulfilled' && prefRes.value.data.preferences) {
          const p = prefRes.value.data.preferences;
          if (p.lattice_spacing !== undefined) setLatticeSpacing(p.lattice_spacing);
          if (p.lattice_width !== undefined) setLatticeWidth(p.lattice_width);
        }
      } catch (e) { /* ignore */ }
    };
    loadPrefs();
  }, []);

  // ======= Step 1: Upload & Auto-Analyze =======
  const onDrop = useCallback((accepted) => {
    if (accepted.length > 0) {
      const f = accepted[0];
      if (f.name.toLowerCase().endsWith('.stl')) {
        setFile(f);
        setPreviewFile(URL.createObjectURL(f));
        setError('');
        setSupportResult(null);
        setAnalysisResult(null);
        setRepairResult(null);
        // Auto-trigger analysis
        triggerAnalysis(f);
      } else {
        setError('Please upload a valid STL file');
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/sla': ['.stl'], 'model/stl': ['.stl'] },
    maxFiles: 1,
    maxSize: 50 * 1024 * 1024,
    noClick: currentStep !== STEPS.UPLOAD,
    noDrag: currentStep !== STEPS.UPLOAD,
  });

  const triggerAnalysis = async (uploadFile) => {
    setCurrentStep(STEPS.ANALYZING);
    setError('');

    try {
      const uploadRes = await stlAPI.upload(uploadFile);
      const { analysis_id } = uploadRes.data;

      // Poll for analysis completion
      let attempts = 0;
      const poll = setInterval(async () => {
        try {
          const res = await stlAPI.analyze(analysis_id);
          const data = res.data;

          if (data.status === 'completed') {
            clearInterval(poll);
            setAnalysisResult(data);

            // Check for issues
            const issues = data.ai_report?.issues || [];
            const hasMeshErrors = issues.length > 0;
            setHasIssues(hasMeshErrors);
            setCurrentStep(STEPS.ANALYSIS_RESULT);
          } else if (data.status === 'failed') {
            clearInterval(poll);
            setError(data.error_message || 'Analysis failed');
            setCurrentStep(STEPS.UPLOAD);
          } else {
            attempts++;
            if (attempts > 30) {
              clearInterval(poll);
              setError('Analysis timed out');
              setCurrentStep(STEPS.UPLOAD);
            }
          }
        } catch (e) {
          attempts++;
          if (attempts > 10) {
            clearInterval(poll);
            setError('Failed to get analysis results');
            setCurrentStep(STEPS.UPLOAD);
          }
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed');
      setCurrentStep(STEPS.UPLOAD);
    }
  };

  // ======= Step 3: Repair =======
  const triggerRepair = async () => {
    if (!file) return;
    setCurrentStep(STEPS.REPAIRING);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await api.post('/stl/repair/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { repair_id } = uploadRes.data;

      let attempts = 0;
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/stl/repair/${repair_id}`);
          const repair = res.data;

          if (repair.status === 'completed') {
            clearInterval(poll);
            setRepairResult(repair);
            setHasIssues(false);
            setCurrentStep(isRepairOnly ? STEPS.REPAIR_DONE : STEPS.CONFIGURE);
          } else if (repair.status === 'failed') {
            clearInterval(poll);
            setError(repair.error_message || 'Repair failed');
            setCurrentStep(STEPS.ANALYSIS_RESULT);
          } else {
            attempts++;
            if (attempts > 60) {
              clearInterval(poll);
              setError('Repair timed out');
              setCurrentStep(STEPS.ANALYSIS_RESULT);
            }
          }
        } catch (e) {
          attempts++;
          if (attempts > 10) {
            clearInterval(poll);
            setError('Failed to check repair status');
            setCurrentStep(STEPS.ANALYSIS_RESULT);
          }
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Repair upload failed');
      setCurrentStep(STEPS.ANALYSIS_RESULT);
    }
  };

  // ======= Step 5: Generate Supports =======
  const triggerGenerate = async () => {
    if (!file) return;
    setCurrentStep(STEPS.GENERATING);
    setError('');
    const startTime = Date.now();

    const progressStages = [
      { time: 0, stage: 'Uploading model...', percent: 5 },
      { time: 3, stage: 'Analyzing geometry...', percent: 12 },
      { time: 10, stage: 'Detecting overhangs...', percent: 22 },
      { time: 25, stage: 'Building tree structure...', percent: 35 },
      { time: 45, stage: 'Generating lattice...', percent: 50 },
      { time: 70, stage: 'Creating branches...', percent: 65 },
      { time: 100, stage: 'Optimizing mesh...', percent: 75 },
      { time: 140, stage: 'Building interface...', percent: 83 },
      { time: 180, stage: 'Refining geometry...', percent: 88 },
      { time: 240, stage: 'Almost done...', percent: 93 },
      { time: 360, stage: 'Finalizing...', percent: 96 },
    ];

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', 'pro');
      formData.append('tree_style', treeStyle);
      formData.append('density', density);
      formData.append('optimize_orientation', optimizeOrientation ? 'true' : 'false');
      if (!optimizeOrientation) {
        formData.append('rotation_x', manualRotation.x.toString());
        formData.append('rotation_y', manualRotation.y.toString());
        formData.append('rotation_z', manualRotation.z.toString());
      }
      formData.append('lattice_spacing', latticeSpacing.toString());
      formData.append('lattice_width', latticeWidth.toString());

      const uploadRes = await api.post('/supports/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      trackFileUpload('stl', file?.size || 0);
      trackSupportGeneration(mode, treeStyle);

      const { support_id } = uploadRes.data;

      // Poll for status
      let attempts = 0;
      let networkErrors = 0;
      // Total timeout: 15 minutes (450 attempts × 2s). Complex models on
      // production hardware can legitimately take 5-10 minutes — we'd rather
      // wait longer than falsely time out a valid generation.
      const MAX_POLL_ATTEMPTS = 450;
      const MAX_NETWORK_ERRORS = 20;  // ~40s of consecutive failures before giving up
      const poll = setInterval(async () => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        let stage = progressStages[0];
        for (const s of progressStages) {
          if (elapsed >= s.time) stage = s;
        }
        setProgressInfo({ stage: stage.stage, percent: Math.min(stage.percent, 95), elapsed });

        try {
          const res = await api.get(`/supports/${support_id}`);
          const job = res.data;
          networkErrors = 0;

          if (job.status === 'completed') {
            clearInterval(poll);
            // Fetch the full record (contact_points, analysis, etc.) only
            // once we know it's ready — avoids hammering production with
            // 500KB+ responses every 2 seconds during the polling phase.
            try {
              const fullRes = await api.get(`/supports/${support_id}?full=true`);
              setProgressInfo({ stage: 'Complete!', percent: 100, elapsed });
              setSupportResult(fullRes.data);
              setCurrentStep(STEPS.RESULTS);
            } catch (fullErr) {
              // Fall back to the lightweight record if full fetch fails
              setProgressInfo({ stage: 'Complete!', percent: 100, elapsed });
              setSupportResult(job);
              setCurrentStep(STEPS.RESULTS);
            }
          } else if (job.status === 'failed') {
            clearInterval(poll);
            setError(job.error_message || 'Support generation failed');
            setCurrentStep(STEPS.CONFIGURE);
          }
        } catch (e) {
          networkErrors++;
          if (networkErrors >= MAX_NETWORK_ERRORS) {
            clearInterval(poll);
            setError('Lost connection to server');
            setCurrentStep(STEPS.CONFIGURE);
          }
        }

        attempts++;
        if (attempts >= MAX_POLL_ATTEMPTS) {
          clearInterval(poll);
          setError('Generation timed out after 15 minutes. Please try a simpler model or contact support.');
          setCurrentStep(STEPS.CONFIGURE);
        }
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start support generation');
      setCurrentStep(STEPS.CONFIGURE);
    }
  };

  // ======= Download Handlers =======
  const handleDownload = (format) => {
    if (!supportResult) return;
    trackDownload(format, 'full-workflow');
    const token = localStorage.getItem('token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/supports/${supportResult.id}/download/${format}?token=${token}`;
    window.open(url, '_blank');
  };

  // ======= Quote PDF =======
  const downloadQuotePDF = async () => {
    if (!supportResult) return;
    setQuoteLoading(true);
    try {
      const response = await api.post(
        `/supports/${supportResult.id}/quote-pdf`,
        {
          project_name: quoteName || file?.name?.replace('.stl', ''),
          markup_percent: quoteMarkup,
          notes: quoteNotes,
        },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quoteName || 'quote'}_quote.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowQuoteModal(false);
    } catch (err) {
      setError('Failed to generate quote PDF');
    } finally {
      setQuoteLoading(false);
    }
  };

  // ======= Reset =======
  const handleReset = () => {
    if (previewFile) URL.revokeObjectURL(previewFile);
    setFile(null);
    setPreviewFile(null);
    setManualRotation({ x: 0, y: 0, z: 0 });
    setAnalysisResult(null);
    setRepairResult(null);
    setSupportResult(null);
    setError('');
    setHasIssues(false);
    setCurrentStep(STEPS.UPLOAD);
    setProgressInfo({ stage: '', percent: 0, elapsed: 0 });
  };

  // ======= Step indicator helper =======
  const getStepState = (stepIdx) => {
    const stepMap = { 0: 0, 1: 1, 2: 1, 3: 2, 4: 2, 5: 3, 6: 3, 7: 3 };
    const totalSteps = isRepairOnly ? 3 : 4;
    const activeIdx = stepMap[currentStep] ?? 0;

    if (stepIdx < activeIdx) return 'done';
    if (stepIdx === activeIdx) return 'active';
    return 'pending';
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="unified-workflow">
      {/* Email Verification Gate */}
      {user && !user.is_verified && (
        <EmailVerificationBanner />
      )}
      {/* Onboarding Tour */}
      <OnboardingTour show={currentStep === STEPS.UPLOAD && !isRepairOnly} />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            {isRepairOnly ? 'Repair & Analysis' : 'AI Support Engine'}
          </h1>
          <p className="text-text-secondary text-sm mt-1">
            {isRepairOnly
              ? 'Upload, analyze, and repair your STL models'
              : 'Upload, analyze, repair, and generate supports — all in one flow'}
          </p>
        </div>
        {currentStep > STEPS.UPLOAD && (
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 bg-bg-light border border-text-muted/20 text-text-secondary hover:text-text-primary rounded-lg transition text-sm"
            data-testid="reset-workflow-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Start Over
          </button>
        )}
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-0 bg-bg-light rounded-xl p-3 border border-text-muted/20">
        {(isRepairOnly ? stepLabelsRepair : stepLabelsAll).map((step, i) => {
          const state = getStepState(i);
          const Icon = step.icon;
          return (
            <React.Fragment key={step.key}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition ${
                    state === 'done'
                      ? 'bg-green-500 text-white'
                      : state === 'active'
                      ? 'bg-primary text-white'
                      : 'bg-bg-dark text-text-muted'
                  }`}
                >
                  {state === 'done' ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span
                  className={`text-sm font-medium truncate ${
                    state === 'done'
                      ? 'text-green-400'
                      : state === 'active'
                      ? 'text-text-primary'
                      : 'text-text-muted'
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {i < (isRepairOnly ? stepLabelsRepair : stepLabelsAll).length - 1 && (
                <ChevronRight className="w-4 h-4 text-text-muted/40 flex-shrink-0 mx-1" />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Error Display */}
      <QuotaErrorBanner error={error} onClose={() => setError('')} source="workflow" />

      {/* ============ STEP 1: Upload ============ */}
      {currentStep === STEPS.UPLOAD && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-16 text-center cursor-pointer transition ${
            isDragActive
              ? 'border-primary bg-primary/5'
              : file
              ? 'border-accent bg-accent/5'
              : 'border-text-muted/20 bg-bg-light hover:border-primary/40'
          }`}
          data-testid="upload-dropzone"
        >
          <input {...getInputProps()} />
          <Upload className={`w-14 h-14 mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-text-muted'}`} />
          <p className="text-lg font-semibold text-text-primary mb-1">
            {isDragActive ? 'Drop your STL file here' : 'Drag & drop your STL file'}
          </p>
          <p className="text-text-secondary text-sm mb-3">or click to browse (max 50MB)</p>
          <p className="text-xs text-text-muted">Analysis starts automatically after upload</p>
        </div>
      )}

      {/* ============ STEP 2: Analyzing ============ */}
      {currentStep === STEPS.ANALYZING && (
        <div className="bg-bg-light rounded-xl p-12 text-center border border-primary/20">
          <Loader className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Analyzing {file?.name}...</h3>
          <p className="text-text-secondary text-sm">Checking geometry, printability, and mesh issues</p>
        </div>
      )}

      {/* ============ STEP 3: Analysis Results ============ */}
      {currentStep === STEPS.ANALYSIS_RESULT && analysisResult && (
        <div className="space-y-4">
          {/* Printability Score */}
          <div className="bg-bg-light rounded-xl p-5 border border-text-muted/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <h3 className="text-lg font-semibold text-text-primary">Analysis Complete</h3>
              </div>
              <div className="text-2xl font-bold text-text-primary">
                {analysisResult.ai_report?.printability_score || 85}/100
              </div>
            </div>
            <div className="w-full bg-bg-dark rounded-full h-2">
              <div
                className="bg-gradient-to-r from-primary to-accent h-2 rounded-full"
                style={{ width: `${analysisResult.ai_report?.printability_score || 85}%` }}
              />
            </div>
            {/* Geometry Summary */}
            {analysisResult.geometry_data && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {[
                  { label: 'Faces', value: analysisResult.geometry_data.num_faces?.toLocaleString() },
                  { label: 'Vertices', value: analysisResult.geometry_data.num_vertices?.toLocaleString() },
                  { label: 'Volume', value: `${analysisResult.geometry_data.volume?.toFixed(1)} mm\u00B3` },
                  { label: 'Area', value: `${analysisResult.geometry_data.surface_area?.toFixed(1)} mm\u00B2` },
                ].map((g) => (
                  <div key={g.label} className="bg-bg-dark/50 p-2.5 rounded-lg">
                    <p className="text-xs text-text-muted">{g.label}</p>
                    <p className="text-sm font-semibold text-text-primary">{g.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Issues */}
          {hasIssues && analysisResult.ai_report?.issues?.length > 0 && (
            <div className="bg-bg-light rounded-xl p-5 border border-yellow-500/20">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <h3 className="font-semibold text-text-primary">Mesh Issues Detected</h3>
              </div>
              <ul className="space-y-1.5 mb-4">
                {analysisResult.ai_report.issues.map((issue, i) => (
                  <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                    <span className="text-yellow-400 mt-0.5">-</span>
                    <span>{issue}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-3">
                <button
                  onClick={triggerRepair}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition"
                  data-testid="repair-now-btn"
                >
                  <Wrench className="w-4 h-4" />
                  Repair Now
                </button>
                <button
                  onClick={() => setCurrentStep(isRepairOnly ? STEPS.REPAIR_DONE : STEPS.CONFIGURE)}
                  className="px-4 py-2.5 text-text-secondary hover:text-text-primary text-sm transition"
                  data-testid="skip-repair-btn"
                >
                  {isRepairOnly ? 'Skip repair' : 'Skip, generate anyway'}
                </button>
              </div>
            </div>
          )}

          {/* Fixes / Recommendations */}
          {analysisResult.ai_report?.fixes?.length > 0 && (
            <div className="bg-bg-light rounded-xl p-5 border border-accent/20">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle className="w-5 h-5 text-accent" />
                <h3 className="font-semibold text-text-primary">Recommendations</h3>
              </div>
              <ul className="space-y-1.5">
                {analysisResult.ai_report.fixes.map((fix, i) => (
                  <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                    <span className="text-accent mt-0.5">-</span>
                    <span>{fix}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* No issues — proceed */}
          {!hasIssues && (
            <div className="bg-bg-light rounded-xl p-5 border border-green-500/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span className="text-text-primary font-medium">
                    {isRepairOnly ? 'Model is clean — no repairs needed' : 'Model is clean — ready for supports'}
                  </span>
                </div>
                {isRepairOnly ? (
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
                    data-testid="analyze-another-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Analyze Another
                  </button>
                ) : (
                  <button
                    onClick={() => setCurrentStep(STEPS.CONFIGURE)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
                    data-testid="proceed-to-generate-btn"
                  >
                    Configure Supports
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============ STEP 4: Repairing ============ */}
      {currentStep === STEPS.REPAIRING && (
        <div className="bg-bg-light rounded-xl p-12 text-center border border-blue-500/20">
          <Loader className="w-10 h-10 text-blue-400 animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-text-primary mb-2">Repairing Mesh...</h3>
          <p className="text-text-secondary text-sm">Fixing geometry issues for optimal printing</p>
        </div>
      )}

      {/* ============ REPAIR DONE (repair-only mode) ============ */}
      {currentStep === STEPS.REPAIR_DONE && repairResult && (
        <div className="space-y-4">
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Repair Complete</h3>
                <p className="text-sm text-text-secondary">
                  {repairResult.issues_found || 0} issues fixed, {repairResult.repairs_made || 0} repairs applied
                </p>
              </div>
            </div>

            {repairResult.repair_details && (
              <div className="bg-bg-dark/30 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-text-primary mb-2">Repair Summary</h4>
                <ul className="space-y-1">
                  {Object.entries(repairResult.repair_details).map(([key, value]) => (
                    <li key={key} className="text-sm text-text-secondary flex items-center justify-between">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-text-primary font-medium">{String(value)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Download repaired file */}
            {repairResult.id && (
              <button
                onClick={() => {
                  const token = localStorage.getItem('token');
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/stl/repair/${repairResult.id}/download?token=${token}`;
                  window.open(url, '_blank');
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
                data-testid="download-repaired-btn"
              >
                <Download className="w-4 h-4" />
                Download Repaired STL
              </button>
            )}
          </div>

          {/* Geometry comparison */}
          {analysisResult?.geometry_data && (
            <div className="bg-bg-light rounded-xl p-5 border border-text-muted/20">
              <h4 className="text-sm font-semibold text-text-primary mb-3">Original Model Geometry</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Faces', value: analysisResult.geometry_data.num_faces?.toLocaleString() },
                  { label: 'Vertices', value: analysisResult.geometry_data.num_vertices?.toLocaleString() },
                  { label: 'Volume', value: `${analysisResult.geometry_data.volume?.toFixed(1)} mm\u00B3` },
                  { label: 'Area', value: `${analysisResult.geometry_data.surface_area?.toFixed(1)} mm\u00B2` },
                ].map((g) => (
                  <div key={g.label} className="bg-bg-dark/50 p-2.5 rounded-lg">
                    <p className="text-xs text-text-muted">{g.label}</p>
                    <p className="text-sm font-semibold text-text-primary">{g.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-5 py-2.5 bg-bg-light border border-text-muted/20 text-text-secondary hover:text-text-primary rounded-lg transition"
              data-testid="analyze-another-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Analyze Another
            </button>
          </div>
        </div>
      )}

      {/* ============ STEP 5: Configure & Generate ============ */}
      {currentStep === STEPS.CONFIGURE && (
        <div className="space-y-4">
          {/* Repair success banner */}
          {repairResult && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <div>
                <p className="text-text-primary font-medium text-sm">Mesh repaired successfully</p>
                <p className="text-text-secondary text-xs">
                  {repairResult.issues_found || 0} issues fixed, {repairResult.repairs_made || 0} repairs applied
                </p>
              </div>
            </div>
          )}

          {/* File Info */}
          <div className="bg-bg-light rounded-xl p-4 border border-text-muted/20 flex items-center gap-3">
            <FileText className="w-5 h-5 text-primary" />
            <span className="text-text-primary font-medium text-sm flex-1 truncate">{file?.name}</span>
            <span className="text-xs text-text-muted">{(file?.size / 1024 / 1024).toFixed(1)} MB</span>
          </div>

          {/* Support Configuration */}
          <div className="bg-bg-light rounded-xl p-5 border border-text-muted/20 space-y-5">
            <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              Support Configuration
            </h3>

            {/* Tree Style */}
            <div>
              <label className="text-sm text-text-secondary mb-2 block">Tree Style</label>
              <div className="grid grid-cols-2 gap-3">
                {/* Forest — Active */}
                <button
                  onClick={() => setTreeStyle('forest')}
                  className="p-3 rounded-lg border text-left transition border-primary bg-primary/10"
                  data-testid="tree-style-forest"
                >
                  <p className="text-sm font-medium text-text-primary">Forest</p>
                  <p className="text-xs text-text-secondary mt-0.5">Multiple independent trees</p>
                </button>
                {/* Hierarchical — Coming Soon */}
                <div
                  className="p-3 rounded-lg border border-text-muted/10 bg-bg-dark/30 text-left opacity-60 cursor-not-allowed"
                  data-testid="tree-style-hierarchical"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-text-muted">Hierarchical</p>
                    <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded-full font-semibold tracking-wide uppercase">Coming Soon</span>
                  </div>
                  <p className="text-xs text-text-muted mt-0.5">Single branching trunk</p>
                </div>
              </div>
            </div>

            {/* Orientation */}
            <OrientationControls
              optimizeOrientation={optimizeOrientation}
              setOptimizeOrientation={setOptimizeOrientation}
              manualRotation={manualRotation}
              setManualRotation={setManualRotation}
              previewFile={previewFile}
              file={file}
            />

            {/* Advanced */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-text-secondary hover:text-text-primary transition flex items-center gap-1"
            >
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
              Advanced settings
            </button>

            {showAdvanced && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-bg-dark/30 rounded-lg">
                <div>
                  <label className="text-xs text-text-muted block mb-1">Density</label>
                  <select
                    value={density}
                    onChange={(e) => setDensity(e.target.value)}
                    className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="sparse">Sparse</option>
                    <option value="standard">Standard</option>
                    <option value="dense">Dense</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Lattice Spacing (mm)</label>
                  <input
                    type="number"
                    value={latticeSpacing}
                    onChange={(e) => setLatticeSpacing(parseFloat(e.target.value) || 2)}
                    step="0.5"
                    min="1"
                    max="5"
                    className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted block mb-1">Lattice Width (mm)</label>
                  <input
                    type="number"
                    value={latticeWidth}
                    onChange={(e) => setLatticeWidth(parseFloat(e.target.value) || 0.6)}
                    step="0.1"
                    min="0.3"
                    max="2"
                    className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}

            {/* Generate Button */}
            <button
              onClick={triggerGenerate}
              className="w-full py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
              data-testid="generate-supports-btn"
            >
              <Layers className="w-5 h-5" />
              Generate Supports
            </button>
          </div>
        </div>
      )}

      {/* ============ STEP 6: Generating ============ */}
      {currentStep === STEPS.GENERATING && (
        <div className="bg-bg-light rounded-xl p-8 border border-primary/20 text-center space-y-4">
          <Loader className="w-10 h-10 text-primary animate-spin mx-auto" />
          <h3 className="text-lg font-semibold text-text-primary">{progressInfo.stage || 'Starting...'}</h3>
          <div className="w-full bg-bg-dark rounded-full h-2.5 max-w-md mx-auto">
            <div
              className="bg-gradient-to-r from-primary to-accent h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${progressInfo.percent}%` }}
            />
          </div>
          <p className="text-sm text-text-muted">Elapsed: {progressInfo.elapsed}s</p>
        </div>
      )}

      {/* ============ STEP 7: Results ============ */}
      {currentStep === STEPS.RESULTS && supportResult && (
        <div className="space-y-5">
          {/* Success Banner */}
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <div>
                <h3 className="text-lg font-semibold text-text-primary">Supports Generated Successfully</h3>
                <p className="text-sm text-text-secondary">
                  {supportResult.tree_style} mode | {supportResult.processing_time?.toFixed(1)}s processing time
                </p>
              </div>
            </div>

            {/* Download buttons */}
            <div className="flex flex-wrap gap-3 mt-4">
              <button
                onClick={() => handleDownload('stl')}
                className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition"
                data-testid="download-stl-btn"
              >
                <Download className="w-4 h-4" />
                Download STL
              </button>
              <button
                onClick={() => handleDownload('3mf')}
                className="flex items-center gap-2 px-5 py-2.5 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition"
                data-testid="download-3mf-btn"
              >
                <Download className="w-4 h-4" />
                Download 3MF
              </button>
              <button
                onClick={() => {
                  if (isFreePlan) {
                    setShowUpgradeHint(true);
                    return;
                  }
                  setQuoteName(file?.name?.replace('.stl', '') || '');
                  setShowQuoteModal(true);
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium transition ${
                  isFreePlan
                    ? 'bg-emerald-600/40 text-white/60 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                }`}
                data-testid="export-quote-btn"
              >
                {isFreePlan ? <Lock className="w-4 h-4" /> : <FileDown className="w-4 h-4" />}
                Export Quote PDF
                {isFreePlan && <span className="text-[10px] bg-white/15 px-1.5 py-0.5 rounded ml-1">PRO</span>}
              </button>
            </div>
          </div>

          {/* ROI Card */}
          <div className="bg-bg-light rounded-xl p-5 border border-green-500/20">
            <ROICard supportResult={supportResult} customPrices={customPrices} />
          </div>

          {/* AI Explanation */}
          {supportResult.ai_explanation && (
            <div className="bg-bg-light rounded-xl p-5 border border-text-muted/20">
              <h3 className="text-base font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" />
                AI Analysis
              </h3>
              {typeof supportResult.ai_explanation === 'string' ? (
                <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                  {supportResult.ai_explanation}
                </p>
              ) : (
                <div className="space-y-3">
                  {supportResult.ai_explanation.summary && (
                    <p className="text-sm text-text-primary font-medium">{supportResult.ai_explanation.summary}</p>
                  )}
                  {supportResult.ai_explanation.why_supports_needed && (
                    <div>
                      <p className="text-xs text-text-muted uppercase mb-1">Why Supports Needed</p>
                      <p className="text-sm text-text-secondary">{supportResult.ai_explanation.why_supports_needed}</p>
                    </div>
                  )}
                  {supportResult.ai_explanation.optimization_strategy && (
                    <div>
                      <p className="text-xs text-text-muted uppercase mb-1">Optimization Strategy</p>
                      <p className="text-sm text-text-secondary">{supportResult.ai_explanation.optimization_strategy}</p>
                    </div>
                  )}
                  {supportResult.ai_explanation.material_efficiency && (
                    <div>
                      <p className="text-xs text-text-muted uppercase mb-1">Material Efficiency</p>
                      <p className="text-sm text-text-secondary">{supportResult.ai_explanation.material_efficiency}</p>
                    </div>
                  )}
                  {supportResult.ai_explanation.removal_tips && Array.isArray(supportResult.ai_explanation.removal_tips) && (
                    <div>
                      <p className="text-xs text-text-muted uppercase mb-1">Removal Tips</p>
                      <ul className="text-sm text-text-secondary list-disc list-inside">
                        {supportResult.ai_explanation.removal_tips.map((tip, i) => (
                          <li key={i}>{tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {supportResult.ai_explanation.recommendations && Array.isArray(supportResult.ai_explanation.recommendations) && (
                    <div>
                      <p className="text-xs text-text-muted uppercase mb-1">Recommendations</p>
                      <ul className="text-sm text-text-secondary list-disc list-inside">
                        {supportResult.ai_explanation.recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Support Details */}
          {supportResult.support_strategy && (
            <div className="bg-bg-light rounded-xl p-5 border border-text-muted/20">
              <h3 className="text-base font-semibold text-text-primary mb-3">Support Details</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Support Count', value: supportResult.support_count || 0 },
                  { label: 'Tree Style', value: supportResult.tree_style },
                  { label: 'Volume', value: `${supportResult.cost_estimate?.support_volume_mm3?.toFixed(0) || 0} mm\u00B3` },
                  { label: 'Material', value: supportResult.estimated_material_saved || 'N/A' },
                ].map((d) => (
                  <div key={d.label} className="bg-bg-dark/50 p-3 rounded-lg">
                    <p className="text-xs text-text-muted">{d.label}</p>
                    <p className="text-sm font-semibold text-text-primary capitalize">{d.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Easy-Peel Heatmap */}
          {supportResult.contact_points && supportResult.contact_points.length > 0 && (
            <div data-testid="heatmap-summary" className="bg-bg-light rounded-xl p-5 border border-text-muted/20">
              <h3 className="text-base font-semibold text-text-primary mb-3 flex items-center gap-2">
                <Flame className="w-4 h-4 text-red-400" />
                Easy-Peel Heatmap
              </h3>
              <p className="text-xs text-text-muted mb-3">
                Contact point analysis — where supports touch your model and how difficult they are to remove.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-bg-dark/50 p-3 rounded-lg text-center">
                  <div className="w-3 h-3 rounded-full bg-green-500 mx-auto mb-1.5" />
                  <p className="text-green-400 text-xl font-bold">
                    {supportResult.contact_points.filter(p => p.label === 'easy').length}
                  </p>
                  <p className="text-[10px] text-text-muted">Easy peel</p>
                </div>
                <div className="bg-bg-dark/50 p-3 rounded-lg text-center">
                  <div className="w-3 h-3 rounded-full bg-orange-500 mx-auto mb-1.5" />
                  <p className="text-orange-400 text-xl font-bold">
                    {supportResult.contact_points.filter(p => p.label === 'moderate').length}
                  </p>
                  <p className="text-[10px] text-text-muted">Moderate</p>
                </div>
                <div className="bg-bg-dark/50 p-3 rounded-lg text-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 mx-auto mb-1.5" />
                  <p className="text-red-400 text-xl font-bold">
                    {supportResult.contact_points.filter(p => p.label === 'hard').length}
                  </p>
                  <p className="text-[10px] text-text-muted">Hard</p>
                </div>
              </div>
              <button
                onClick={() => setShow3DPreview(true)}
                className="w-full mt-3 py-2 bg-primary/15 text-primary text-sm font-medium rounded-lg hover:bg-primary/25 transition flex items-center justify-center gap-2"
                data-testid="open-heatmap-3d-btn"
              >
                <Eye className="w-4 h-4" />
                View Heatmap in 3D
              </button>
            </div>
          )}

          {/* Start New */}
          <button
            onClick={handleReset}
            className="w-full py-3 bg-bg-light border border-text-muted/20 text-text-secondary hover:text-text-primary rounded-xl transition flex items-center justify-center gap-2"
            data-testid="new-project-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Process Another Model
          </button>
        </div>
      )}

      {/* ============ Upgrade Hint Modal ============ */}
      {showUpgradeHint && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="upgrade-modal">
          <div className="bg-bg-light rounded-xl border border-primary/30 w-full max-w-sm p-6 space-y-4 text-center">
            <div className="w-12 h-12 bg-primary/15 rounded-full flex items-center justify-center mx-auto">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-lg font-bold text-text-primary">Pro Feature</h3>
            <p className="text-sm text-text-secondary leading-relaxed">
              Professional PDF quote export is available on the <span className="text-primary font-semibold">Pro</span> and <span className="text-accent font-semibold">Studio</span> plans.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => setShowUpgradeHint(false)}
                className="flex-1 px-4 py-2.5 bg-bg-dark border border-text-muted/20 text-text-secondary rounded-lg text-sm transition hover:text-text-primary"
                data-testid="upgrade-dismiss-btn"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowUpgradeHint(false);
                  window.location.href = '/#pricing';
                }}
                className="flex-1 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium text-sm transition"
                data-testid="upgrade-cta-btn"
              >
                View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ Quote PDF Modal ============ */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-light rounded-xl border border-text-muted/20 w-full max-w-md p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
                <FileDown className="w-5 h-5 text-emerald-400" />
                Export Quote PDF
              </h3>
              <button onClick={() => setShowQuoteModal(false)}>
                <X className="w-5 h-5 text-text-muted hover:text-text-primary" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Project Name</label>
                <input
                  type="text"
                  value={quoteName}
                  onChange={(e) => setQuoteName(e.target.value)}
                  placeholder="Enter project name"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="quote-project-name"
                />
              </div>

              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Markup Percentage (%)</label>
                <input
                  type="number"
                  value={quoteMarkup}
                  onChange={(e) => setQuoteMarkup(parseInt(e.target.value) || 0)}
                  min="0"
                  max="500"
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50"
                  data-testid="quote-markup"
                />
                <p className="text-xs text-text-muted mt-1">Applied on top of material + electricity costs</p>
              </div>

              <div>
                <label className="text-sm text-text-secondary block mb-1.5">Notes (optional)</label>
                <textarea
                  value={quoteNotes}
                  onChange={(e) => setQuoteNotes(e.target.value)}
                  placeholder="Add custom notes to the quote..."
                  rows={3}
                  className="w-full bg-bg-dark border border-text-muted/20 text-text-primary rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 resize-none"
                  data-testid="quote-notes"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={downloadQuotePDF}
                disabled={quoteLoading}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition disabled:opacity-50"
                data-testid="generate-quote-pdf-btn"
              >
                <FileDown className="w-4 h-4" />
                {quoteLoading ? 'Generating...' : 'Download PDF'}
              </button>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2.5 text-text-secondary hover:text-text-primary transition text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 3D Preview with Heatmap */}
      {show3DPreview && supportResult && (
        <STLViewerWithControls
          modelUrl={`${process.env.REACT_APP_BACKEND_URL}/api/supports/${supportResult.id}/download/model-only?token=${localStorage.getItem('token')}`}
          supportUrl={`${process.env.REACT_APP_BACKEND_URL}/api/supports/${supportResult.id}/download/support-only?token=${localStorage.getItem('token')}`}
          onClose={() => setShow3DPreview(false)}
          contactPoints={supportResult.contact_points || []}
        />
      )}
    </div>
  );
};

export default UnifiedWorkflow;
